/**
 * EMI calculator core logic.
 * EMI = P * R * (1+R)^N / ((1+R)^N - 1)
 *  - P: principal
 *  - R: monthly rate (annual% / 12 / 100)
 *  - N: number of monthly installments
 */

export interface EMIInputs {
  principal: number; // BDT
  annualRatePct: number; // e.g. 12 for 12%
  months: number; // tenure in months
  startDate?: Date;
}

export interface MonthRow {
  index: number; // 1-based
  date: Date;
  emi: number;
  principal: number;
  interest: number;
  balance: number;
}

export interface YearRow {
  year: number; // calendar year
  principalPaid: number;
  interestPaid: number;
  balance: number;
}

export interface EMIResult {
  emi: number;
  totalInterest: number;
  totalPayment: number;
  schedule: MonthRow[];
  yearly: YearRow[];
}

export type EMIError =
  | "amount_too_small"
  | "tenure_zero"
  | "rate_negative"
  | "missing";

export function validateEMI(inp: Partial<EMIInputs>): { ok: true } | { ok: false; error: EMIError } {
  if (inp.principal == null || inp.annualRatePct == null || inp.months == null) {
    return { ok: false, error: "missing" };
  }
  if (inp.principal < 1000) return { ok: false, error: "amount_too_small" };
  if (inp.months < 1) return { ok: false, error: "tenure_zero" };
  if (inp.annualRatePct < 0) return { ok: false, error: "rate_negative" };
  return { ok: true };
}

export function monthlyEMI(principal: number, annualRatePct: number, months: number): number {
  if (months <= 0) return 0;
  const r = annualRatePct / 12 / 100;
  if (r === 0) return principal / months;
  const pow = Math.pow(1 + r, months);
  return (principal * r * pow) / (pow - 1);
}

export function computeEMI(inp: EMIInputs): EMIResult {
  const emi = monthlyEMI(inp.principal, inp.annualRatePct, inp.months);
  const r = inp.annualRatePct / 12 / 100;

  const schedule: MonthRow[] = [];
  let balance = inp.principal;
  const start = inp.startDate ?? new Date();

  for (let i = 1; i <= inp.months; i++) {
    const interest = balance * r;
    let principalPart = emi - interest;
    if (i === inp.months) {
      // absorb rounding into final principal so balance ends at 0
      principalPart = balance;
    }
    const newBalance = Math.max(0, balance - principalPart);
    const d = new Date(start.getFullYear(), start.getMonth() + (i - 1), 1);
    schedule.push({
      index: i,
      date: d,
      emi: i === inp.months ? principalPart + interest : emi,
      principal: principalPart,
      interest,
      balance: newBalance,
    });
    balance = newBalance;
  }

  // Yearly aggregation by calendar year
  const yearMap = new Map<number, YearRow>();
  for (const row of schedule) {
    const y = row.date.getFullYear();
    const cur = yearMap.get(y) ?? { year: y, principalPaid: 0, interestPaid: 0, balance: row.balance };
    cur.principalPaid += row.principal;
    cur.interestPaid += row.interest;
    cur.balance = row.balance;
    yearMap.set(y, cur);
  }
  const yearly = Array.from(yearMap.values()).sort((a, b) => a.year - b.year);

  const totalPayment = schedule.reduce((s, r2) => s + r2.emi, 0);
  const totalInterest = totalPayment - inp.principal;

  return { emi, totalInterest, totalPayment, schedule, yearly };
}

/** CSV export for the amortization schedule. */
export function scheduleToCSV(schedule: MonthRow[]): string {
  const header = "Month,Date,EMI,Principal,Interest,Balance";
  const rows = schedule.map((r) =>
    [
      r.index,
      `${r.date.getFullYear()}-${String(r.date.getMonth() + 1).padStart(2, "0")}`,
      r.emi.toFixed(2),
      r.principal.toFixed(2),
      r.interest.toFixed(2),
      r.balance.toFixed(2),
    ].join(","),
  );
  return [header, ...rows].join("\n");
}
